import { Trans } from "@lingui/react/macro";
import React from "react";
import { CruiseItineraryFragment } from "types/graphql-types";
import {
  CategorisedShorex,
  CruiseItineraryItemWithExcursions,
  PrePostItem,
  RemoteConfirmedExcursions,
  RemoteConfirmedExcursionsPerItineraryItem,
  RemoteConfirmedShorexPerItineraryItem,
  RemoteGetExcursionsQueryResponse,
  RemoteLandProgramme,
  RemoteShorex,
  ShorexCategory
} from "./types";
import useLiveSanityContent from "../../../ssc-api/useLiveSanityContent";
import { UseFetchDataResponse } from "src/ssc-api/useFetchApi";
import { isPresent } from "src/utils/checks";
import { DayByDay } from "src/page-components/cruise/itinerary/day-by-day";
import { InclusionContent } from "@silversea-ssc/ui-components-react";

export const formatArrivalDepartureTime = (
  arrivalTime?: string,
  departureTime?: string
): JSX.Element | string => {
  if (!arrivalTime && !departureTime) return "";
  if (arrivalTime && departureTime) return `${arrivalTime} - ${departureTime}`;

  if (arrivalTime)
    return (
      <React.Fragment>
        <Trans>Arrival</Trans> {arrivalTime}
      </React.Fragment>
    );
  if (departureTime)
    return (
      <React.Fragment>
        <Trans>Departure</Trans> {departureTime}
      </React.Fragment>
    );

  return "";
};

const excursionSorter = (a, b) => {
  return ("" + a.code).localeCompare(b.code);
};

const shouldHideDurationAndCode = (shorex: RemoteShorex): boolean =>
  // Old stricter logic which they might want back if users get confused:
  // shorex.category === "Expedition – Included Activities";
  shorex.category?.startsWith("Expedition") || false;

const getCategorisedShorexes = (
  shorexes: RemoteShorex[],
  confirmedShorexes: RemoteConfirmedShorexPerItineraryItem
): CategorisedShorex[] =>
  (confirmedShorexes || [])
    .map((confirmedShorex) => {
      const shorex = shorexes?.find(
        (x) => x.shorexWebCode === confirmedShorex.shorexWebCode
      );
      if (!shorex) return undefined;

      return {
        ...shorex,
        displayCategory:
          shorex.category === "Mid Land Program"
            ? ShorexCategory.MidVoyageLandAdventure
            : confirmedShorex.includedInAllInclusive
              ? ShorexCategory.IncludedInAllInclusive
              : ShorexCategory.Premium, // SELECTED
        duration: confirmedShorex.duration,
        price: confirmedShorex.price,
        hideDurationAndCode: shouldHideDurationAndCode(shorex)
      };
    })
    .filter(isPresent)
    .sort(excursionSorter);

const getUnbundlingCategorisedShorexes = (
  shorexes: RemoteShorex[],
  confirmedShorexes: RemoteConfirmedShorexPerItineraryItem
): CategorisedShorex[] =>
  (confirmedShorexes || [])
    .map((confirmedShorex) => {
      const shorex = shorexes?.find(
        (x) => x.shorexWebCode === confirmedShorex.shorexWebCode
      );
      if (!shorex) return undefined;

      return {
        ...shorex,
        displayCategory: undefined,
        duration: confirmedShorex.duration,
        price: confirmedShorex.price,
        hideDurationAndCode: shouldHideDurationAndCode(shorex)
      };
    })
    .filter(isPresent)
    .sort(excursionSorter);

export function useRemoteGetExcursions(
  cruiseCode: string | undefined,
  portCodes: string[] | undefined = []
): UseFetchDataResponse<RemoteGetExcursionsQueryResponse> {
  const query = `
    {
      "shorexes": *[_type == "shorex" && port._ref in (${JSON.stringify(
        portCodes
      )})]|order(code){
        _id,
        "portId": port._ref,
        shorexWebCode,
        code,
        name,
        activityLevel,
        category,
        subcategory,
        accessibility,
        features
      },

      "confirmedExcursions": *[_type == "cruiseData" && cruiseCode == ${JSON.stringify(
        cruiseCode
      )}]{
        itinerary[]{
          "shorexes": confirmedShorexes[]{
            _key,
            includedInAllInclusive,
            shorexWebCode,
            duration,
            price,
            fareCodes
          },
          "itineraryItem": _key,
          "portId": port{_ref}._ref}
        }[].itinerary[]
      }
`;

  const { data, status, isLoading, isError } = useLiveSanityContent<{
    shorexes: RemoteShorex[];
    landProgrammes: RemoteLandProgramme[];
    confirmedExcursions: RemoteConfirmedExcursions[];
  }>(query);

  if (data?.result) {
    return {
      isLoading,
      isError,
      data: {
        shorexes: data.result.shorexes,
        confirmedExcursions: fareCodesOrEmptyArray(
          data.result.confirmedExcursions
        )
      },
      status
    };
  }

  return {
    isLoading,
    isError,
    data: { shorexes: [], confirmedExcursions: [] },
    status
  };
}

const fareCodesOrEmptyArray = (
  confirmedExcursions: RemoteConfirmedExcursions[]
): RemoteConfirmedExcursions[] =>
  confirmedExcursions.map((x) => ({
    ...x,
    shorexes: x.shorexes.map((s) => ({
      ...s,
      fareCodes: s.fareCodes || []
    }))
  }));

function confirmedExcursionsPerItineraryItem(
  itineraryItemKey: string,
  confirmedExcursions: RemoteConfirmedExcursions[]
): RemoteConfirmedExcursionsPerItineraryItem {
  const excursionsPerItinerary = confirmedExcursions?.find(
    (s) => s.itineraryItem == itineraryItemKey
  );

  return {
    confirmedShorexes: excursionsPerItinerary?.shorexes || []
  };
}

export function buildItineraryWithExcursions(
  itinerary: CruiseItineraryFragment[],
  shorexes: RemoteShorex[],
  confirmedExcursions: RemoteConfirmedExcursions[],
  isUnbundlingEnabled: boolean
): CruiseItineraryItemWithExcursions[] {
  return itinerary.map<CruiseItineraryItemWithExcursions>((itineraryItem) => {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const key = itineraryItem._key!;
    const confirmedExcursionsPerItinerary = confirmedExcursionsPerItineraryItem(
      key,
      confirmedExcursions
    );

    const shorexCategorizator = isUnbundlingEnabled
      ? getUnbundlingCategorisedShorexes
      : getCategorisedShorexes;
    const categorisedShorexes = shorexCategorizator(
      shorexes,
      confirmedExcursionsPerItinerary.confirmedShorexes
    );

    return {
      ...itineraryItem,
      shorexes: categorisedShorexes || []
    };
  });
}

export const buildPrePost = (
  prePosts: readonly DayByDay[],
  inclusionsContent: readonly InclusionContent[]
): readonly PrePostItem[] =>
  prePosts
    .map((prePost) => {
      const content = inclusionsContent.find((x) =>
        x.codes.some((inclusionCode) => inclusionCode === prePost.inclusionCode)
      );
      return content ? { ...prePost, content } : undefined;
    })
    .filter(isPresent);
